<template>
    <v-card rounded="lg" class="gantt-card">
        <v-card-title class="d-flex align-center flex-wrap ga-1">
            <span class="text-body-1">{{ $t('inspectionSchedule') }}</span>
            <v-spacer></v-spacer>

            <v-combobox :model-value="selectedStatuses"
                        @update:model-value="handleStatusChange"
                        :items="assetStatuses"
                        item-title="title"
                        item-value="value"
                        :label="$t('status')"
                        variant="outlined"
                        density="compact"
                        hide-details
                        multiple
                        class="mx-2"
                        style="max-width: 320px;">
                <template v-slot:selection="{ item }">
                    <v-chip :color="item.raw.color"
                            :text="item.raw.title"
                            size="small"
                            variant="flat"
                            label
                            closable
                            @click:close="removeStatus(item.raw)"></v-chip>
                </template>

                <template v-slot:item="{ props: itemProps, item }">
                    <v-list-item v-bind="itemProps">
                        <template v-slot:title>
                            <v-chip :color="item.raw.color"
                                    :text="item.raw.title"
                                    size="small"
                                    variant="flat"
                                    label></v-chip>
                        </template>
                    </v-list-item>
                </template>
            </v-combobox>

            <v-divider vertical class="mx-2" style="height: 24px;"></v-divider>

            <v-btn icon flat size="small" @click="expandAll" :title="$t('expandAll')">
                <v-icon>mdi-unfold-more-horizontal</v-icon>
            </v-btn>
            <v-btn icon flat size="small" @click="collapseAll" :title="$t('collapseAll')">
                <v-icon>mdi-unfold-less-horizontal</v-icon>
            </v-btn>

            <v-divider vertical class="mx-2" style="height: 24px;"></v-divider>

            <v-btn icon flat size="small" @click="zoomIn" :title="$t('zoomIn')">
                <v-icon>mdi-magnify-plus</v-icon>
            </v-btn>
            <v-btn icon flat size="small" @click="zoomOut" :title="$t('zoomOut')">
                <v-icon>mdi-magnify-minus</v-icon>
            </v-btn>
            <v-btn icon flat size="small" @click="zoomToFit" :title="$t('zoomToFit')">
                <v-icon>mdi-fit-to-page</v-icon>
            </v-btn>
        </v-card-title>

        <v-card-text class="pa-0">
            <div ref="ganttContainer"
                 class="gantt-container"
                 :class="{ 'gantt-dark': isDark }">
                <v-overlay v-if="isLoading" :model-value="true" contained class="d-flex align-center justify-center">
                    <v-progress-circular indeterminate size="48" color="primary"></v-progress-circular>
                </v-overlay>
            </div>
        </v-card-text>
    </v-card>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { Gantt } from '@bryntum/gantt';
import '@bryntum/gantt/gantt.classic-light.css';
import { sqlLiteral } from '@/utils/assetFields';
import { formatDate } from '@/utils/formatters';

// Inspection history as a timeline, three levels deep and lazily loaded:
//
//   Category -> Asset ID -> lifecycle records
//
// A record's bar spans its creation to the date it was inspected or closed,
// coloured by the record's status. Selecting a row cross-filters the rest of
// the dashboard.
const store = useStore();
const { t } = useI18n();

const chartName = 'inspectionGantt';
const ASSET_PAGE_SIZE = 200;

const filterContext = computed(() => store.state.filterContext);
const isDark = computed(() => store.state.theme === 'dark');

const ganttContainer = ref(null);
let gantt = null;

const isLoading = ref(true);
const assetStatuses = ref([]);
const selectedStatuses = ref([]);

// Ids must be unique across lazily loaded branches; a module counter avoids
// the collisions a Date.now() seed per branch can produce.
let nextTaskId = 1;
const newTaskId = () => nextTaskId++;

// Keys of the nodes the user had open, so an external filter change can
// restore the tree instead of collapsing it.
const expandedKeys = ref(new Set());
const loadingStates = new Map();
// Set while this component is the one changing the filter, so its own
// filtersLastUpdated bump does not trigger a reload.
let applyingOwnFilter = false;

const runQuery = async (query) => {
    const conn = store.getters.getDBConnection;
    if (!conn) return [];
    const result = await conn.query(query);
    return result.toArray().map((row) => row.toJSON());
};

// Arrow hands dates back as epoch millis or Date objects; Bryntum wants a Date
// or an ISO day string.
const toDate = (value) => {
    if (value === null || value === undefined) return null;
    const date = value instanceof Date ? value : new Date(Number(value) || value);
    return Number.isNaN(date.getTime()) ? null : date;
};

// Restrict every level to the statuses the user kept selected.
const statusClause = (alias) => {
    if (!selectedStatuses.value || selectedStatuses.value.length === 0) return '';
    const titles = selectedStatuses.value.map((status) => sqlLiteral(status.title)).join(', ');
    return ` AND ${alias}.statusId IN (SELECT Id FROM status_templates WHERE Title IN (${titles}))`;
};

const loadStatuses = async () => {
    const rows = await runQuery(`
        SELECT DISTINCT s.Title AS value, s.Title AS title, s.Color AS color, s.Sort AS sort
        FROM status_templates s
        INNER JOIN asset_items ai ON s.Id = ai.statusId
        ORDER BY s.Sort
    `);
    assetStatuses.value = rows;
    if (selectedStatuses.value.length === 0) {
        selectedStatuses.value = [...rows];
    }
};

// --- level loaders ------------------------------------------------------

const loadCategories = async () => {
    const rows = await runQuery(`
        SELECT
            COALESCE(a.assetCategory, ${sqlLiteral(t('uncategorised'))}) AS categoryTitle,
            COUNT(*) AS assetCount,
            COUNT(*) FILTER (WHERE a.isOverdue) AS overdueCount,
            MIN(a.createdDate) AS startDate,
            MAX(COALESCE(a.nextInspectionDate, a.latestInspectionDate, CAST(a.createdDate AS DATE))) AS endDate
        FROM filteredAssets a
        GROUP BY 1
        ORDER BY 1
    `);

    return rows.map((row) => ({
        id: newTaskId(),
        name: `${row.categoryTitle} (${Number(row.assetCount)})`,
        type: 'category',
        categoryTitle: row.categoryTitle,
        startDate: toDate(row.startDate),
        endDate: toDate(row.endDate),
        overdueCount: Number(row.overdueCount) || 0,
        children: true,
        childrenPending: Number(row.assetCount) > 0,
        loaded: false,
        expanded: false,
        leaf: Number(row.assetCount) === 0,
    }));
};

const loadAssetsForCategory = async (categoryTitle, offset = 0) => {
    const rows = await runQuery(`
        SELECT
            a.assetKey,
            COALESCE(a.assetRefId, a.assetKey) AS label,
            a.createdDate AS startDate,
            COALESCE(a.nextInspectionDate, a.latestInspectionDate, CAST(a.createdDate AS DATE)) AS endDate,
            a.nextInspectionDate,
            a.latestInspectionDate,
            a.isOverdue,
            a.itemCount,
            st.Title AS statusTitle,
            st.Color AS statusColor
        FROM filteredAssets a
        LEFT JOIN status_templates st ON st.Id = a.assetStatusId
        WHERE COALESCE(a.assetCategory, ${sqlLiteral(t('uncategorised'))}) = ${sqlLiteral(categoryTitle)}
        ORDER BY regexp_replace(COALESCE(a.assetRefId, a.assetKey), '-[^-]*$', '') ASC NULLS LAST,
                 TRY_CAST(regexp_extract(COALESCE(a.assetRefId, a.assetKey), '-([^-]*)$', 1) AS BIGINT) ASC NULLS LAST
        LIMIT ${ASSET_PAGE_SIZE} OFFSET ${offset}
    `);

    const tasks = rows.map((row) => ({
        id: newTaskId(),
        name: row.label,
        type: 'asset',
        assetKey: row.assetKey,
        categoryTitle,
        startDate: toDate(row.startDate),
        endDate: toDate(row.endDate),
        nextInspectionDate: toDate(row.nextInspectionDate),
        statusTitle: row.statusTitle,
        statusColor: row.statusColor,
        isOverdue: Boolean(row.isOverdue),
        itemCount: Number(row.itemCount) || 0,
        children: true,
        childrenPending: Number(row.itemCount) > 0,
        loaded: false,
        expanded: false,
        leaf: Number(row.itemCount) === 0,
    }));

    // A "load more" row rather than pulling thousands of assets at once.
    if (rows.length === ASSET_PAGE_SIZE) {
        tasks.push({
            id: newTaskId(),
            name: t('loadMore'),
            type: 'loadMore',
            categoryTitle,
            offset: offset + ASSET_PAGE_SIZE,
            leaf: true,
            loaded: true,
            childrenPending: false,
        });
    }

    return tasks;
};

const loadItemsForAsset = async (assetKey) => {
    const rows = await runQuery(`
        SELECT
            i.id,
            i.itemSeq,
            i.itemKind,
            i.createdDate AS startDate,
            COALESCE(i.effectiveInspectionDate, CAST(i.closedDate AS DATE), CURRENT_DATE) AS endDate,
            i.nextInspectionDate,
            i.isClosed,
            st.Title AS statusTitle,
            st.Color AS statusColor
        FROM filteredItems i
        LEFT JOIN status_templates st ON st.Id = i.statusId
        WHERE i.assetKey = ${sqlLiteral(assetKey)}${statusClause('i')}
        ORDER BY i.createdDate
    `);

    return rows.map((row) => ({
        id: newTaskId(),
        name: t(`itemKind_${row.itemKind}`),
        type: 'item',
        itemId: row.id,
        assetKey,
        startDate: toDate(row.startDate),
        endDate: toDate(row.endDate),
        nextInspectionDate: toDate(row.nextInspectionDate),
        statusTitle: row.statusTitle,
        statusColor: row.statusColor,
        leaf: true,
        loaded: true,
        childrenPending: false,
    }));
};

// Fetch and attach a node's children the first time it is opened.
const ensureChildren = async (record) => {
    if (!record || !record.get('childrenPending') || record.get('loaded')) return;
    const children = await loadChildrenLazy(record);
    if (children.length > 0) {
        record.appendChild(children);
        record.set('loaded', true);
        if (!record.isExpanded(gantt?.project?.taskStore)) {
            await gantt?.project?.taskStore?.expand(record);
        }
    } else {
        // Nothing came back (e.g. the status filter excluded every record).
        record.set({ loaded: true, childrenPending: false });
    }
};

const loadChildrenLazy = async (record) => {
    const key = `${record.get('type')}:${record.id}`;
    if (loadingStates.get(key)) return [];
    loadingStates.set(key, true);
    try {
        switch (record.get('type')) {
            case 'category': return await loadAssetsForCategory(record.get('categoryTitle'));
            case 'asset': return await loadItemsForAsset(record.get('assetKey'));
            default: return [];
        }
    } finally {
        loadingStates.delete(key);
    }
};

// --- cross-filter -------------------------------------------------------

const applySelectionFilter = async (record) => {
    const context = filterContext.value;
    if (!context) return;

    applyingOwnFilter = true;
    try {
        if (!record) {
            await context.removeFilter(chartName);
            return;
        }
        const type = record.get('type');
        if (type === 'category') {
            const category = record.get('categoryTitle');
            await context.updateFilterWithCondition(
                chartName,
                `COALESCE(assetCategory, ${sqlLiteral(t('uncategorised'))}) = ${sqlLiteral(category)}`,
                `${t('category')}: ${category}`
            );
        } else if (type === 'asset' || type === 'item') {
            const assetKey = record.get('assetKey');
            await context.updateFilterWithCondition(
                chartName,
                `assetKey = ${sqlLiteral(assetKey)}`,
                `${t('assetId')}: ${record.get('type') === 'asset' ? record.get('name') : assetKey}`
            );
        } else {
            await context.removeFilter(chartName);
        }
    } finally {
        applyingOwnFilter = false;
    }
};

// --- chart lifecycle ----------------------------------------------------

const timeSpan = async () => {
    const rows = await runQuery(`
        SELECT
            MIN(createdDate) AS startDate,
            MAX(COALESCE(nextInspectionDate, latestInspectionDate, CAST(createdDate AS DATE))) AS endDate
        FROM filteredAssets
    `);
    const row = rows[0] || {};
    return { start: toDate(row.startDate), end: toDate(row.endDate) };
};

// Tick granularity that suits the overall span.
const presetForSpan = (start, end) => {
    if (!start || !end) return 'monthAndYear';
    const days = Math.max(1, (end - start) / 86400000);
    if (days <= 14) return 'dayAndWeek';
    if (days <= 60) return 'weekAndDayLetter';
    if (days <= 180) return 'weekAndMonth';
    if (days <= 1100) return 'monthAndYear';
    return 'year';
};

const buildGantt = async (categories, span) => {
    if (gantt) {
        gantt.destroy();
        gantt = null;
    }

    gantt = new Gantt({
        appendTo: ganttContainer.value,
        scrollTaskIntoViewOnCellClick: true,
        rowHeight: 34,
        barMargin: 6,
        viewPreset: presetForSpan(span.start, span.end),
        columns: [
            { type: 'name', field: 'name', text: t('ganttName'), width: 260 },
            { type: 'startdate', text: t('ganttCreated'), width: 120 },
            { type: 'enddate', text: t('ganttInspected'), width: 120 },
            {
                text: t('status'),
                field: 'statusTitle',
                width: 130,
                // Returning a DOM config rather than an HTML string: Bryntum
                // escapes strings by default, and both the title and the colour
                // come from customer data.
                renderer: ({ record, value }) => {
                    if (!value) return '';
                    return {
                        tag: 'span',
                        class: 'gantt-status',
                        style: { backgroundColor: record.get('statusColor') || '#888' },
                        text: value,
                    };
                },
            },
            {
                text: t('nextInspection'),
                field: 'nextInspectionDate',
                width: 130,
                renderer: ({ record, value }) => {
                    if (!value) return '';
                    return {
                        tag: 'span',
                        class: record.get('isOverdue') ? 'gantt-overdue' : '',
                        text: formatDate(value),
                    };
                },
            },
        ],
        features: {
            taskEdit: false,
            taskDrag: false,
            taskResize: false,
            taskDragCreate: false,
            criticalPaths: false,
            progressLine: false,
            rollups: false,
            baselines: false,
            dependencies: false,
            rowReorder: false,
            filter: true,
        },
        // Leaf records carry the status colour; parent summary bars keep the
        // Bryntum default so the hierarchy stays readable.
        taskRenderer({ taskRecord, renderData }) {
            const color = taskRecord.get('statusColor');
            if (taskRecord.get('type') === 'item' && color) {
                renderData.style = `background-color: ${color}`;
            }
        },
        project: {
            taskStore: {
                tree: true,
                fields: [
                    'id', 'name', 'startDate', 'endDate', 'parentId', 'type',
                    'categoryTitle', 'assetKey', 'itemId', 'statusColor', 'statusTitle',
                    'nextInspectionDate', 'isOverdue', 'itemCount', 'overdueCount',
                    'childrenPending', 'loaded', 'offset',
                ],
                rootNode: { expanded: true, children: categories },
            },
        },
        listeners: {
            // beforeToggleNode is preventable and resolves synchronously, so it
            // cannot await the fetch; expandNode fires after the node is open
            // and is the one that actually appends. Both are kept because a
            // click on the cell can expand without firing the toggle event.
            beforeToggleNode: ({ record, expanded }) => {
                if (expanded) expandedKeys.value.add(nodeKey(record));
                else expandedKeys.value.delete(nodeKey(record));
            },
            expandNode: ({ record }) => ensureChildren(record),
            toggleNode: ({ record, expanded }) => {
                if (expanded) ensureChildren(record);
            },
            cellClick: async ({ record }) => {
                // "Load more" appends the next page in place of itself.
                if (record.get('type') === 'loadMore') {
                    const parent = record.parent;
                    const more = await loadAssetsForCategory(record.get('categoryTitle'), record.get('offset'));
                    record.remove();
                    if (more.length > 0) parent.appendChild(more);
                    return;
                }
                ensureChildren(record);
            },
            selectionChange: async ({ selected }) => {
                await applySelectionFilter(selected.length > 0 ? selected[0] : null);
            },
        },
    });
};

const nodeKey = (record) => {
    const type = record.get('type');
    if (type === 'category') return `c:${record.get('categoryTitle')}`;
    if (type === 'asset') return `a:${record.get('assetKey')}`;
    return `i:${record.get('itemId')}`;
};

// Re-open the nodes the user had expanded before a filter-driven reload.
const restoreExpansion = async () => {
    if (!gantt || expandedKeys.value.size === 0) return;
    const taskStore = gantt.project.taskStore;
    for (const record of [...taskStore.rootNode.children]) {
        if (expandedKeys.value.has(nodeKey(record))) {
            await ensureChildren(record);
        }
    }
};

const loadData = async () => {
    if (!ganttContainer.value) return;
    isLoading.value = true;
    try {
        const [categories, span] = await Promise.all([loadCategories(), timeSpan()]);
        await buildGantt(categories, span);
        await restoreExpansion();
    } catch (error) {
        console.error('Error building the inspection schedule:', error);
    } finally {
        isLoading.value = false;
    }
};

// --- toolbar ------------------------------------------------------------

const expandAll = () => gantt?.expandAll();
const collapseAll = () => {
    expandedKeys.value.clear();
    gantt?.collapseAll();
};
const zoomIn = () => gantt?.zoomIn();
const zoomOut = () => gantt?.zoomOut();
const zoomToFit = async () => {
    if (!gantt) return;
    const span = await timeSpan();
    if (span.start && span.end) {
        gantt.setTimeSpan(span.start, span.end);
        gantt.viewPreset = presetForSpan(span.start, span.end);
        gantt.scrollToDate(span.start, { block: 'start', animate: false });
    }
};

const handleStatusChange = (value) => {
    selectedStatuses.value = value;
    loadData();
};

const removeStatus = (status) => {
    selectedStatuses.value = selectedStatuses.value.filter((item) => item.title !== status.title);
    loadData();
};

// --- wiring -------------------------------------------------------------

onMounted(async () => {
    if (store.getters.getDataLoaded) {
        await loadStatuses();
        await loadData();
    }
});

onUnmounted(() => {
    // Route switches would otherwise leak the Gantt instance and its listeners.
    if (gantt) {
        gantt.destroy();
        gantt = null;
    }
});

watch(
    () => store.getters.getDataLoaded,
    async (loaded) => {
        if (loaded) {
            await loadStatuses();
            await loadData();
        }
    }
);

watch(
    () => filterContext.value?.filtersLastUpdated?.value,
    () => {
        if (applyingOwnFilter) return;
        if (store.getters.getDataLoaded) loadData();
    }
);
</script>

<style scoped>
/* Bryntum lays the timeline out at its natural width (years of quarters), which
   as a flex item would push the whole page wider than the viewport. Pinning the
   card and the container to their parent's width - and letting Bryntum scroll
   the timeline internally - keeps the page at one screen. */
.gantt-card {
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
}

.gantt-container {
    height: 600px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    position: relative;
    overflow: hidden;
}
</style>

<style>
/* Bryntum renders into its own DOM subtree, so these cannot be scoped. */
.gantt-status {
    display: inline-block;
    padding: 1px 8px;
    border-radius: 10px;
    color: #fff;
    font-size: 11px;
    line-height: 18px;
}

.gantt-overdue {
    color: #d03b3b;
    font-weight: 600;
}

/* The classic-light theme assumes a light page; soften it on dark. */
.gantt-dark .b-gantt {
    --b-gantt-background-color: #263e4e;
    filter: invert(0.92) hue-rotate(180deg);
}

.gantt-dark .b-gantt .b-gantt-task,
.gantt-dark .b-gantt img {
    filter: invert(1) hue-rotate(180deg);
}
</style>
